import { Button, buttonVariants } from '@flexui-io/flexui-components';
import { Link, useParams } from '@tanstack/react-router';
import { Headset, Home, LayoutDashboard, LogIn, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { sessionRecoveryHref } from '../auth/session-guard';
import { ErrorIllustration } from './error-illustration';
import { getErrorCopy, type ErrorStateKind } from './error-state-config';

import './error-state.css';

type ErrorStateProps = {
  kind: ErrorStateKind;
  onRetry?: () => void;
  requestId?: string | undefined;
};

// Link must stay a link for navigation: use the exact same FlexUI button
// variants as the actual <Button> used for retry, plus Slider's semantic tokens.
const primaryLinkClass = buttonVariants({
  variant: 'primary', size: 'md', className: 'slider-ui-button',
});
const supportLinkClass = buttonVariants({
  variant: 'outline', size: 'md', className: 'slider-ui-button',
});

export function ErrorState({ kind, onRetry, requestId }: ErrorStateProps) {
  const { i18n } = useTranslation();
  const params = useParams({ strict: false });
  const companySlug = params.companySlug;
  const copy = getErrorCopy(kind, i18n.resolvedLanguage ?? i18n.language);
  const retryable = kind === '500' || kind === 'unexpected';
  const supportSubject = encodeURIComponent('Slider support - ' + kind);

  const dashboard = companySlug ? (
    <Link className={primaryLinkClass} data-slider-tone="primary" data-slider-priority="recovery"
      params={{ companySlug }} to="/manage/$companySlug/dashboard">
      {kind === '404' ? <Home aria-hidden="true" size={18} /> :
        <LayoutDashboard aria-hidden="true" size={18} />}
      <span>{copy.primary}</span>
    </Link>
  ) : (
    <Link className={primaryLinkClass} data-slider-tone="primary" data-slider-priority="recovery"
      to={kind === '404' ? '/' : '/manage'}>
      {kind === '404' ? <Home aria-hidden="true" size={18} /> :
        <LayoutDashboard aria-hidden="true" size={18} />}
      <span>{copy.primary}</span>
    </Link>
  );

  return (
    <section aria-labelledby="slider-error-title" className="slider-error-state"
      dir={i18n.dir()} data-error-kind={kind}>
      <div className="slider-error-center">
        <ErrorIllustration kind={kind} />
        <h1 id="slider-error-title">{copy.title}</h1>
        <p className="slider-error-description">{copy.description}</p>
        <div className="slider-error-actions">
          {kind === '401' ? (
            <a className={primaryLinkClass} data-slider-tone="primary" data-slider-priority="recovery"
              href={sessionRecoveryHref(
                typeof window === 'undefined' ? '/manage' : window.location.pathname,
              )}>
              <LogIn aria-hidden="true" size={18} />
              <span>{copy.primary}</span>
            </a>
          ) : retryable ? (
            <Button className="slider-ui-button" data-slider-tone="primary" data-slider-priority="recovery"
              onClick={onRetry ?? (() => window.location.reload())}
              size="md" type="button" variant="primary">
              <RefreshCw aria-hidden="true" size={18} />
              <span>{copy.primary}</span>
            </Button>
          ) : dashboard}
          <a className={supportLinkClass} data-slider-tone="secondary"
            href={'mailto:info@raadco.com.au?subject=' + supportSubject}>
            <Headset aria-hidden="true" size={18} />
            <span>{copy.support}</span>
          </a>
        </div>
        {requestId ? (
          <p className="slider-error-request-id">
            {copy.requestId}: <bdi dir="ltr">{requestId}</bdi>
          </p>
        ) : null}
      </div>
    </section>
  );
}
